import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge, Box, Button, ButtonGroup, Flex, SimpleGrid, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr, VStack,
} from "@chakra-ui/react";
import { RefreshCw } from "lucide-react";
import { Line } from "react-chartjs-2";
import { getMetricasAnunciosExperimento } from "../../services/api";
import { ErroCarga, Kpi, VERDE, Vazio, dataHoraBR, diaBR, num } from "../SaudeAnunciosML/comum";
import {
  COR_CLASSICO, COR_PREMIUM, COR_REFERENCIA, Cartao, CartaoGrafico, SeletorDias, TAMANHO_PEQUENO,
  brl, linhaVerticalEm, opcoesLinha, pctFracao, serieLinha,
} from "./base";

const METRICAS = [
  { chave: "visitas", label: "Visitas", formatar: (v) => num(v), porDia: (v) => num(v, 1) },
  { chave: "unidades", label: "Vendas (un.)", formatar: (v) => num(v), porDia: (v) => num(v, 1) },
  { chave: "pedidos", label: "Pedidos", formatar: (v) => num(v), porDia: (v) => num(v, 1) },
  { chave: "receita", label: "Receita", formatar: (v) => brl(v, 0), porDia: (v) => brl(v) },
  { chave: "lucro", label: "Lucro", formatar: (v) => brl(v, 0), porDia: (v) => brl(v) },
];

const VEREDITO = {
  ganhando: { label: "Ganhando", curto: "Ganhando", cor: "green" },
  perdendo: { label: "Perdendo", curto: "Perdendo", cor: "red" },
  inconclusivo: { label: "Ainda inconclusivo", curto: "Inconclusivo", cor: "gray" },
};

function SeloVeredito({ veredito, curto = false }) {
  const v = VEREDITO[veredito] || VEREDITO.inconclusivo;
  return <Badge colorScheme={v.cor} fontSize="0.75em" px={2} py={0.5} whiteSpace="nowrap">{curto ? v.curto : v.label}</Badge>;
}

function CartaoMetrica({ metrica, dados }) {
  if (!dados) {
    return <Cartao titulo={metrica.label}><Text fontSize="sm" color="gray.600">Sem dados.</Text></Cartao>;
  }
  const efeito = Number(dados.efeito_total);
  return (
    <Cartao titulo={metrica.label} extra={<SeloVeredito veredito={dados.veredito} />}>
      <SimpleGrid columns={2} spacing={2} fontSize="sm" sx={{ fontVariantNumeric: "tabular-nums" }}>
        <Box>
          <Text fontSize="xs" color={COR_PREMIUM} fontWeight="bold">PREMIUM</Text>
          <Text fontWeight="semibold">{pctFracao(dados.variacao_premium)}</Text>
          <Text fontSize="xs" color="gray.600">{metrica.formatar(dados.premium_antes)} → {metrica.formatar(dados.premium_depois)}</Text>
        </Box>
        <Box>
          <Text fontSize="xs" color={COR_CLASSICO} fontWeight="bold">CLÁSSICO</Text>
          <Text fontWeight="semibold">{pctFracao(dados.variacao_classico)}</Text>
          <Text fontSize="xs" color="gray.600">{metrica.formatar(dados.classico_antes)} → {metrica.formatar(dados.classico_depois)}</Text>
        </Box>
      </SimpleGrid>
      <Box mt={3} pt={3} borderTopWidth="1px" borderColor="gray.100">
        <Text fontSize="xs" color="gray.600">Efeito do Premium no período</Text>
        <Text fontSize="lg" fontWeight="bold" color={efeito > 0 ? "green.600" : efeito < 0 ? "red.600" : "gray.700"}>
          {efeito > 0 ? "+" : ""}{metrica.formatar(efeito)}
          <Text as="span" fontSize="xs" fontWeight="normal" color="gray.600"> ({efeito > 0 ? "+" : ""}{metrica.porDia(dados.efeito_por_dia)}/dia)</Text>
        </Text>
        <Text fontSize="xs" color="gray.600">Faixa de 95%: {metrica.formatar(dados.ic95?.[0])} a {metrica.formatar(dados.ic95?.[1])} · {num(dados.produtos)} produtos</Text>
        {dados.amostra_suficiente === false && <Text fontSize="xs" color="gray.600">Amostra ainda pequena para concluir.</Text>}
      </Box>
    </Cartao>
  );
}

function TabelaQuebra({ titulo, linhas }) {
  return (
    <Cartao titulo={titulo}>
      <Box overflowX="auto">
        <Table size="sm" sx={{ fontVariantNumeric: "tabular-nums" }}>
          <Thead>
            <Tr>
              <Th>Grupo</Th>
              <Th isNumeric>Produtos</Th>
              <Th isNumeric>Visitas</Th>
              <Th isNumeric>Vendas</Th>
              <Th isNumeric>Lucro</Th>
              <Th>Lucro</Th>
            </Tr>
          </Thead>
          <Tbody>
            {linhas.map((l) => (
              <Tr key={l.nome}>
                <Td whiteSpace="nowrap">{l.nome}</Td>
                <Td isNumeric>{num(l.m?.unidades?.produtos)}</Td>
                <Td isNumeric>{l.m?.visitas ? `${Number(l.m.visitas.efeito_total) > 0 ? "+" : ""}${num(l.m.visitas.efeito_total)}` : "—"}</Td>
                <Td isNumeric>{l.m?.unidades ? `${Number(l.m.unidades.efeito_total) > 0 ? "+" : ""}${num(l.m.unidades.efeito_total)}` : "—"}</Td>
                <Td isNumeric>{l.m?.lucro ? brl(l.m.lucro.efeito_total, 0) : "—"}</Td>
                <Td>{l.m?.lucro ? <SeloVeredito veredito={l.m.lucro.veredito} curto /> : "—"}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Cartao>
  );
}

function GraficoSerie({ dados, metrica }) {
  const serie = useMemo(() => dados.serie || [], [dados.serie]);
  const indiceInicio = serie.findIndex((s) => String(s.data).slice(0, 10) >= dados.inicio);
  const chart = useMemo(() => ({
    labels: serie.map((s) => diaBR(s.data)),
    datasets: [
      serieLinha("Premium", serie.map((s) => Number(s.premium?.[metrica.chave] || 0)), COR_PREMIUM),
      serieLinha("Clássico (mesmos produtos)", serie.map((s) => Number(s.classico?.[metrica.chave] || 0)), COR_CLASSICO),
    ],
  }), [serie, metrica]);
  const opcoes = useMemo(() => opcoesLinha(metrica.formatar), [metrica]);
  const plugins = useMemo(() => [linhaVerticalEm(indiceInicio, "Troca")], [indiceInicio]);

  return (
    <CartaoGrafico
      titulo={`${metrica.label} por dia: lado Premium x lado Clássico`}
      grafico={<Line data={chart} options={opcoes} plugins={plugins} role="img" aria-label={`${metrica.label} por dia nos anúncios Premium e Clássico do mesmo produto`} />}
      tabela={(
        <Table size="sm" sx={{ fontVariantNumeric: "tabular-nums" }}>
          <Thead position="sticky" top={0} bg="white"><Tr><Th>Dia</Th><Th isNumeric>Premium</Th><Th isNumeric>Clássico</Th></Tr></Thead>
          <Tbody>
            {[...serie].reverse().map((s) => (
              <Tr key={s.data}>
                <Td>{diaBR(s.data)}</Td>
                <Td isNumeric>{metrica.formatar(s.premium?.[metrica.chave] || 0)}</Td>
                <Td isNumeric>{metrica.formatar(s.classico?.[metrica.chave] || 0)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    />
  );
}

export default function ExperimentoTab() {
  const [janela, setJanela] = useState(30);
  const [metrica, setMetrica] = useState(METRICAS[1]);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(false);
  const ultima = useRef(0);

  const carregar = useCallback(async () => {
    const minha = ++ultima.current;
    setCarregando(true);
    const r = await getMetricasAnunciosExperimento({ janela });
    if (minha !== ultima.current) return;
    setCarregando(false);
    setErro(!r);
    if (r) setDados(r);
  }, [janela]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => () => { ultima.current += 1; }, []);

  const g = dados?.grupos || {};
  const lucro = dados?.geral?.lucro;

  return (
    <Box p={{ base: 3, md: 5 }}>
      <Flex gap={2} mb={2} flexWrap="wrap" align="center">
        <SeletorDias valor={janela} onChange={setJanela} rotulo="Janela antes e depois" />
        <Button size={TAMANHO_PEQUENO} variant="outline" leftIcon={<RefreshCw size={14} />} onClick={carregar} isLoading={carregando}>Atualizar</Button>
        {dados?.calculado_em && <Text fontSize="xs" color="gray.600" ml={{ base: 0, md: "auto" }}>Calculado em {dataHoraBR(dados.calculado_em)}</Text>}
      </Flex>
      <Text fontSize="sm" color="gray.600" mb={4}>
        Cada produto é comparado com ele mesmo na outra conta: Premium de um lado, Clássico do outro, antes e depois de {dados?.inicio ? diaBR(dados.inicio) : "16/10"}.
      </Text>

      {erro && !dados && <ErroCarga onTentar={carregar} carregando={carregando} />}
      {!dados && !erro && <Flex justify="center" py={10}><Spinner color="orange.400" /></Flex>}

      {dados && (
        <VStack align="stretch" spacing={4}>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
            <Kpi label="Premium na conta 1" valor={num(g.premium_conta_1)} ajuda="produtos (SKU ímpar)" cor={COR_PREMIUM} />
            <Kpi label="Premium na conta 2" valor={num(g.premium_conta_2)} ajuda="produtos (SKU par)" cor={COR_PREMIUM} />
            <Kpi label="Clássico nas duas" valor={num(g.ambos_classico)} ajuda="fora da regra (referência)" cor={COR_REFERENCIA} />
            <Kpi label="Dias comparados" valor={num(dados.dias)} ajuda={dados.depois ? `${diaBR(dados.antes?.[0])}–${diaBR(dados.antes?.[1])} x ${diaBR(dados.depois?.[0])}–${diaBR(dados.depois?.[1])}` : "antes x depois"} cor={VERDE} />
          </SimpleGrid>

          {dados.aguardando ? (
            <Vazio titulo="A comparação aparece depois do primeiro dia completo com Premium.">
              Os dias comparados crescem até a janela escolhida, sempre com o mesmo número de dias antes e depois.
            </Vazio>
          ) : (
            <>
              {lucro && (
                <Cartao>
                  <Flex gap={3} align="center" flexWrap="wrap">
                    <SeloVeredito veredito={lucro.veredito} />
                    <Text fontSize="md" fontWeight="bold" color="gray.800">
                      Lucro com o Premium: {Number(lucro.efeito_por_dia) >= 0 ? "+" : ""}{brl(lucro.efeito_por_dia)} por dia
                    </Text>
                    <Text fontSize="sm" color="gray.600">({brl(lucro.efeito_total, 0)} em {num(dados.dias)} dias, comparado ao Clássico do mesmo produto)</Text>
                  </Flex>
                </Cartao>
              )}

              <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
                {METRICAS.map((m) => <CartaoMetrica key={m.chave} metrica={m} dados={dados.geral?.[m.chave]} />)}
              </SimpleGrid>

              <Box>
                <ButtonGroup size={TAMANHO_PEQUENO} isAttached variant="outline" mb={2} flexWrap="wrap">
                  {METRICAS.map((m) => (
                    <Button key={m.chave} onClick={() => setMetrica(m)} isActive={metrica.chave === m.chave}
                      bg={metrica.chave === m.chave ? "orange.50" : "white"}>{m.label}</Button>
                  ))}
                </ButtonGroup>
                <GraficoSerie dados={dados} metrica={metrica} />
              </Box>

              <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
                <TabelaQuebra titulo="Por conta do Premium" linhas={[
                  { nome: "Premium na conta 1", m: dados.por_conta?.["1"] },
                  { nome: "Premium na conta 2", m: dados.por_conta?.["2"] },
                ]} />
                <TabelaQuebra titulo="Por faixa de preço (Clássico)" linhas={(dados.por_faixa || []).map((f) => ({ nome: f.faixa, m: f.metricas }))} />
              </SimpleGrid>

              {dados.referencia_ambos_classico?.unidades && (
                <Cartao titulo="Referência: produtos Clássico nas duas contas">
                  <SimpleGrid columns={{ base: 2, md: 5 }} spacing={3} fontSize="sm" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {METRICAS.map((m) => {
                      const r = dados.referencia_ambos_classico[m.chave] || {};
                      const v = Number(r.antes) > 0 ? (Number(r.depois) - Number(r.antes)) / Number(r.antes) : null;
                      return (
                        <Box key={m.chave}>
                          <Text fontSize="xs" color="gray.600">{m.label}</Text>
                          <Text fontWeight="semibold">{pctFracao(v)}</Text>
                          <Text fontSize="xs" color="gray.600">{m.formatar(r.antes)} → {m.formatar(r.depois)}</Text>
                        </Box>
                      );
                    })}
                  </SimpleGrid>
                </Cartao>
              )}
            </>
          )}
        </VStack>
      )}
    </Box>
  );
}
